// Typed client for the Phase-3 reader-production endpoints. Mirrors the
// fetch/base-url/CSRF conventions of src/lib/lumi-api/client.ts (same `/api/v1`
// mount, `credentials: include` session cookie, and `x-lumi-csrf` header read
// from the `lumi_csrf` cookie on mutating requests). Kept as a standalone
// module so it stays additive to the existing client.

import { LumiApiError } from "@/lib/lumi-api";
import type {
  Bookmark,
  Chapter,
  Highlight,
  ImageBlock,
  ReaderPrefs,
} from "./types";

// ── Response envelopes ─────────────────────────────────────────────────────

interface ListEnvelope<T> {
  items: T[];
}

interface ReaderPrefsEnvelope {
  // The backend returns the raw settings object; callers should normalize it
  // with normalizeReaderPrefs() from ./types.
  settings: Partial<ReaderPrefs> | Record<string, unknown>;
}

interface JobSummaryLite {
  id: string;
  kind: string;
  status: string;
  created_at: string;
}

export interface EnsureTtsResult {
  pending: number;
  job: JobSummaryLite | null;
}

export interface DictionaryEntry {
  id: string;
  book_id: string | null;
  term: string;
  source_language: string | null;
  target_language: string | null;
  definition: string;
  context: string | null;
  created_at: string;
}

// ── Low-level request helpers (match client.ts conventions) ────────────────

function defaultBaseUrl(): string {
  if (typeof window !== "undefined") {
    return process.env.NEXT_PUBLIC_LUMI_API_BASE_URL ?? "";
  }
  return process.env.LUMI_API_BASE_URL ?? "http://127.0.0.1:4000";
}

function csrfTokenFromCookie(): string | null {
  if (typeof document === "undefined") return null;
  return (
    document.cookie
      .split(";")
      .map((part) => part.trim())
      .find((part) => part.startsWith("lumi_csrf="))
      ?.slice("lumi_csrf=".length) ?? null
  );
}

async function toApiError(response: Response): Promise<LumiApiError> {
  let message = `Lumi API request failed with status ${response.status}`;
  let code: string | undefined;
  try {
    const body = (await response.json()) as {
      error?: string;
      message?: string;
    };
    message = body.message ?? message;
    code = body.error;
  } catch {
    // keep status-only message
  }
  return new LumiApiError(message, response.status, code);
}

async function request<T>(
  path: string,
  options: { method?: string; body?: unknown; csrf?: boolean } = {},
): Promise<T> {
  const headers = new Headers();
  if (options.body !== undefined) {
    headers.set("content-type", "application/json");
  }
  if (options.csrf !== false) {
    const token = csrfTokenFromCookie();
    if (token) headers.set("x-lumi-csrf", token);
  }
  const base = defaultBaseUrl().replace(/\/$/, "");
  const response = await fetch(`${base}/api/v1${path}`, {
    method: options.method ?? "GET",
    headers,
    credentials: "include",
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  });
  if (!response.ok) {
    throw await toApiError(response);
  }
  return (await response.json()) as T;
}

// ── Chapters & images ──────────────────────────────────────────────────────

export function getChapters(bookId: string): Promise<Chapter[]> {
  return request<ListEnvelope<Chapter>>(`/books/${bookId}/chapters`).then(
    (r) => r.items,
  );
}

export function getImages(bookId: string): Promise<ImageBlock[]> {
  return request<ListEnvelope<ImageBlock>>(`/books/${bookId}/images`).then(
    (r) => r.items,
  );
}

/** Resolve an image block to an absolute URL hitting the authenticated backend
 *  image route (`/books/:book_id/images/:image_id`). An already-absolute uri
 *  (e.g. a future remote image) is passed through unchanged. */
export function imageBlockUrl(block: ImageBlock): string {
  if (/^https?:\/\//.test(block.uri)) return block.uri;
  const base = defaultBaseUrl().replace(/\/$/, "");
  return `${base}/api/v1/books/${block.book_id}/images/${block.id}`;
}

// ── Reader prefs ───────────────────────────────────────────────────────────

export function getReaderPrefs(): Promise<Partial<ReaderPrefs>> {
  return request<ReaderPrefsEnvelope>(`/me/reader-prefs`).then(
    (r) => (r.settings ?? {}) as Partial<ReaderPrefs>,
  );
}

/** Whole-document replace: send the full settings object. */
export function putReaderPrefs(
  settings: ReaderPrefs,
): Promise<Partial<ReaderPrefs>> {
  return request<ReaderPrefsEnvelope>(`/me/reader-prefs`, {
    method: "PUT",
    body: { settings },
  }).then((r) => (r.settings ?? {}) as Partial<ReaderPrefs>);
}

// ── Bookmarks ──────────────────────────────────────────────────────────────

export function listBookmarks(bookId: string): Promise<Bookmark[]> {
  return request<ListEnvelope<Bookmark>>(`/books/${bookId}/bookmarks`).then(
    (r) => r.items,
  );
}

export function addBookmark(
  bookId: string,
  input: { chunk_index: number; char_offset?: number; label?: string },
): Promise<Bookmark> {
  return request<{ bookmark: Bookmark }>(`/books/${bookId}/bookmarks`, {
    method: "POST",
    body: input,
  }).then((r) => r.bookmark);
}

export function deleteBookmark(bookmarkId: string): Promise<void> {
  return request<{ status: string }>(`/bookmarks/${bookmarkId}`, {
    method: "DELETE",
  }).then(() => undefined);
}

// ── Highlights ─────────────────────────────────────────────────────────────

export function listHighlights(bookId: string): Promise<Highlight[]> {
  return request<ListEnvelope<Highlight>>(`/books/${bookId}/highlights`).then(
    (r) => r.items,
  );
}

export function addHighlight(
  bookId: string,
  input: {
    chunk_index: number;
    start_offset: number;
    end_offset: number;
    color?: string;
    note?: string;
  },
): Promise<Highlight> {
  return request<{ highlight: Highlight }>(`/books/${bookId}/highlights`, {
    method: "POST",
    body: input,
  }).then((r) => r.highlight);
}

export function deleteHighlight(highlightId: string): Promise<void> {
  return request<{ status: string }>(`/highlights/${highlightId}`, {
    method: "DELETE",
  }).then(() => undefined);
}

// ── On-demand TTS (lazy mode) ──────────────────────────────────────────────

/** Ensure audio exists for chunks [fromChunk, fromChunk + count). Idempotent;
 *  returns how many chunks were missing audio and the enqueued job (if any). */
export function ensureTts(
  bookId: string,
  fromChunk: number,
  count: number,
): Promise<EnsureTtsResult> {
  return request<EnsureTtsResult>(`/books/${bookId}/tts/ensure`, {
    method: "POST",
    body: { from_chunk: fromChunk, count },
  });
}

// ── Progress (with stable char offset) ─────────────────────────────────────

export interface SavedProgress {
  book_id: string;
  chunk_index: number;
  char_offset: number;
  updated_at: string;
}

export function saveProgress(
  bookId: string,
  chunkIndex: number,
  charOffset = 0,
): Promise<SavedProgress> {
  return request<{ progress: SavedProgress }>(`/books/${bookId}/progress`, {
    method: "PATCH",
    body: { chunk_index: chunkIndex, char_offset: charOffset },
  }).then((r) => r.progress);
}

// ── Dictionary (context-aware) ─────────────────────────────────────────────

export function lookupDictionary(
  term: string,
  context: string,
  opts: {
    book_id?: string | null;
    source_language?: string | null;
    target_language?: string | null;
  } = {},
): Promise<DictionaryEntry> {
  return request<{ entry: DictionaryEntry }>(`/dictionary`, {
    method: "POST",
    body: {
      term,
      context,
      book_id: opts.book_id ?? null,
      source_language: opts.source_language ?? null,
      target_language: opts.target_language ?? null,
    },
  }).then((r) => r.entry);
}
